<template>
  <a-table
    rowKey="no"
    :columns="columns"
    :dataSource="list"
    :loading="loading"
    :pagination="pagination"
    :scroll="{ y: 'calc(100vh - 280px)' }"
    @change="handleTableChange"
  >
    <template #bodyCell="{ column, record, text }">
      <template v-if="column.key === 'rights_name'">
        {{ displayName(record as unknown as IOrder) }}
      </template>
      <template v-if="column.key === 'buy_num'">
        {{ displayAmount(record as unknown as IOrder) }}
      </template>
      <template v-else-if="column.key === 'price'">
        {{ displayPrice(record as unknown as IOrder) }}
      </template>
      <template v-else-if="column.key === 'pay_fee'">
        {{ formatPrice(record.pay_fee, record.currency).string }}
      </template>
      <template v-else-if="column.key === 'period'">
        <template v-if="record.start_time && !record.expired_time">
          {{ $t('yong-jiu') }}
        </template>
        <template v-else-if="record.start_time && record.expired_time">
          {{ timeFormat(record.start_time, 'YYYY.MM.DD') }} ~
          {{ timeFormat(record.expired_time, 'YYYY.MM.DD') }}
        </template>
        <!-- 待付款的言币包 -->
        <template
          v-else-if="
            record.status === ORDER_STATUS.UNPAID &&
            record.order_type === ORDER_TYPE.CREDITS &&
            record.credit_coins_package_id
          "
        >
          {{ $t('chang-qi-you-xiao') }}
        </template>
        <!-- 待付款其他 -->
        <template v-else-if="record.status === ORDER_STATUS.UNPAID">
          {{ $t('1-nian') }}
        </template>
      </template>
      <div v-else-if="column.key === 'pay_type'">
        {{
          useDictItem(isGlobalVersion() ? 'payTypeEn' : 'payTypeCN', text)
            ?.label
        }}
      </div>
      <div v-else-if="column.key === 'finished_time'">
        {{ timeFormat(text, 'YYYY.MM.DD HH:mm') }}
      </div>
      <template v-else-if="column.key === 'status'">
        <a-tag
          v-if="record.is_available"
          :color="useDictItem('order_status', text)?.color"
        >
          {{ useDictItem('order_status', text)?.label }}
        </a-tag>
        <a-tag v-else-if="!record.is_available" color="#FF5656">
          {{ $t('yi-zuo-fei') }}
        </a-tag>
        <div
          v-if="
            record.pay_type === PAY_TYPE.TRANSFER &&
            record.status === ORDER_STATUS.UNPAID
          "
          class="mt-[4px]"
        >
          {{ $t('ding-dan-1-tian-hou-zi-dong-qu-xiao') }}
        </div>
      </template>
      <div v-else-if="column.key === 'action'">
        <template v-if="checkOrderProcessable(record)">
          <a
            @click="
              useTransferIntercept({
                order: record as IOrder,
                onCancel: () => {
                  reload()
                },
              })
            "
          >
            {{ $t('chu-li-ding-dan') }}
          </a>
          <a-divider type="vertical" />
        </template>
        <a @click="handClick">{{ $t('lian-xi-ke-fu') }}</a>
        <template v-if="record.could_apply_invoice && show">
          <a-divider type="vertical" />
          <router-link
            v-if="!record.is_business_transfer"
            :to="{
              name: PageKey.CENTER_ORDER_INVOICE,
              query: {
                id: record.no,
              },
            }"
          >
            {{ $t('shen-qing-kai-piao') }}
          </router-link>
          <a-popover
            v-else
            placement="topRight"
            :content="
              $t(
                'gai-ding-dan-wei-xian-xia-ding-dan-qing-lian-xi-nin-de-shang-wu-fu-ze-ren-jin-hang-kai-piao'
              )
            "
          >
            <span class="text-font-40 cursor-not-allowed">{{
              $t('shen-qing-kai-piao')
            }}</span>
          </a-popover>
        </template>
        <!-- <template v-if="record.order_type === ORDER_TYPE.PGC">
          <a-divider type="vertical" />
          <nuxt-link
            :to="{
              name: PageKey.PGC,
              query: {
                token: record.no,
              },
            }"
          >
            {{ $t('cha-kan-xiang-qing') }}
          </nuxt-link>
          <nuxt-link> 查看人物 </nuxt-link>
        </template> -->
      </div>
    </template>
    <template #emptyText>
      <EmptyView :class="{ invisible: loading }" />
    </template>
  </a-table>
</template>
<script lang="ts" setup>
import type { TableProps } from 'ant-design-vue'
import { orderList } from '~/api'
import { ORDER_TYPE, PAY_TYPE, type Loose } from '~/api/interface'
import type { IOrder } from '~/api/interface/order'
import { useTransferIntercept } from '~/features/paymentDesk/useTransferIntercept'
import { usePage } from '~/hooks/usePage'
import { useTime } from '~/hooks/useTime'
import FilterDropdown from '@/features/filterDropdown/index.vue'
import { PageKey } from '~/constants/page'

const { timeFormat } = useTime()

const { $t } = useNuxtApp()

const emits = defineEmits(['check'])

defineProps<{
  isVerify?: boolean
  show: boolean
}>()

function checkOrderProcessable(order: IOrder | Loose) {
  return (
    order.status === ORDER_STATUS.UNPAID && order.pay_type === PAY_TYPE.TRANSFER
  )
}

const columns = [
  {
    title: $t('ding-dan-hao'),
    dataIndex: 'no',
    key: 'no',
    width: 200,
  },
  {
    title: $t('shang-pin-xin-xi'),
    dataIndex: 'rights_name',
    key: 'rights_name',
    width: 120,
  },
  {
    title: $t('you-xiao-qi'),
    dataIndex: 'period',
    key: 'period',
    width: 180,
  },
  {
    title: $t('shu-liang'),
    dataIndex: 'buy_num',
    key: 'buy_num',
    width: 100,
  },
  {
    title: $t('dan-jia'),
    dataIndex: 'price',
    key: 'price',
    width: 100,
  },
  {
    title: $t('shi-fu-kuan'),
    dataIndex: 'pay_fee',
    key: 'pay_fee',
    width: 100,
  },
  {
    title: $t('zhi-fu-fang-shi'),
    dataIndex: 'pay_type',
    key: 'pay_type',
    width: 120,
    filterDropdown: (props: any) =>
      h(FilterDropdown, {
        // radio: true,
        type: isGlobalVersion() ? 'payTypeEn' : 'payTypeCN',
        ...props,
      }),
  },
  {
    title: $t('ding-dan-wan-cheng'),
    dataIndex: 'finished_time',
    key: 'finished_time',
    width: 155,
    sorter: true,
  },
  {
    title: $t('zhuang-tai'),
    dataIndex: 'status',
    key: 'status',
    width: 100,
  },
  {
    title: $t('cao-zuo'),
    key: 'action',
    width: 160,
  },
]

const displayName = (record: IOrder) => {
  if (record.order_type === ORDER_TYPE.CREDITS) {
    return record.rights_name + `(${record.rights.total_credit_coin})`
  }
  return record.rights_name
}

const displayAmount = (record: IOrder) => {
  if (record.order_type === ORDER_TYPE.STORAGE) {
    let totalStorage = record.rights.total_storage!
    const step = ['byte', 'KB', 'MB', 'GB', 'TB']
    let lv = 0
    while (totalStorage >= 1024) {
      totalStorage = totalStorage / 1024
      lv += 1
    }
    return `${totalStorage.toFixed(2)}${step[lv]}`
  }
  return record.buy_num
}

const displayPrice = (record: IOrder) => {
  const price = record.pay_fee / record.buy_num
  return formatPrice(Number.isNaN(price) ? 0 : price, record.currency).string
}

const { list, total, page, page_size, loading, getList, reload } =
  usePage<IOrder>({
    api: async (params) => {
      const res: any = await orderList({
        ...params,
        status__in: '1,4,99',
        many: 1,
      })
      if (res?.data?.could_apply_invoice_total_count) {
        emits('check', res.data.could_apply_invoice_total_count)
      }

      return res
    },
    pageSize: 20,
  })

const pagination = computed<any>(() => ({
  total: total.value,
  current: page.value,
  pageSize: page_size.value,
  showTotal: (total: string) => $t('gong-total-tiao', [total]),
  showSizeChanger: true,
  showQuickJumper: true,
}))

const handleTableChange: TableProps['onChange'] = (p, filters, sorter: any) => {
  page.value = p.current as number
  page_size.value = p.pageSize as number
  getList({
    page: p.current,
    page_size: p.pageSize,
    pay_type__in: filters.pay_type?.join(','),
    o: sorter.order
      ? `${sorter.order === 'ascend' ? '' : '-'}${sorter?.field}`
      : undefined,
  })
}

const handClick = () => {
  openCustomerService()
}

onMounted(() => {
  reload()
})
</script>
<style lang="scss" scoped></style>
